import { Injectable } from '@angular/core';
import { ActivatedRouteSnapshot, CanActivate, CanActivateChild, Router, RouterStateSnapshot, UrlTree } from '@angular/router';

/** Rôle issu du claim Firebase (libellé « Admin Ferme » ou code ADMIN_FERME), mis en session à la connexion */
export class RoleUtil {
  private static readonly ADMIN = ['Admin Ferme', 'ADMIN_FERME', 'Admin', 'ADMIN'];

  static role(): string | null {
    try { return sessionStorage.getItem('role'); } catch { return null; }
  }

  static estAdmin(): boolean {
    const r = this.role();
    return !!r && this.ADMIN.includes(r);
  }

  /** ADMIN seulement (pas ADMIN_FERME) : réglages sensibles comme le site web */
  static estAdministrateurPrincipal(): boolean {
    const r = this.role();
    return r === 'Admin' || r === 'ADMIN';
  }

  static estComptable(): boolean {
    const r = this.role();
    return r === 'Comptable' || r === 'COMPTABLE';
  }

  static estGestionnaire(): boolean {
    const r = this.role();
    return r === 'Gestionnaire' || r === 'GESTIONNAIRE';
  }

  /** Agent de terrain : déclare dans ses modules, sans finances ni réglages */
  static estPersonnel(): boolean {
    const r = this.role();
    return r === 'Personnel' || r === 'PERSONNEL';
  }

  /** Rédige les articles du site, sans autre accès */
  static estContenu(): boolean {
    const r = this.role();
    return r === 'Contenu' || r === 'CONTENU';
  }

  /**
   * Gestionnaire limité à ses postes (périmètre MODULES de /api/me) : ses modules, leurs finances et leur
   * personnel ; ni créances, factures, synthèse, exports, GED, abonnement ni paramètres de la ferme.
   */
  static gestionnaireLimite(): boolean {
    try { return this.estGestionnaire() && sessionStorage.getItem('perimetre') === 'MODULES'; } catch { return false; }
  }

  /** Postes du gestionnaire limité (POULAILLER, BASSINS, CHEPTEL, CULTURES) */
  static postes(): string[] {
    try { return JSON.parse(sessionStorage.getItem('postes') || '[]'); } catch { return []; }
  }

  /** Finances complètes de la ferme (trésorerie, créances, factures…) : tous sauf Personnel, Contenu et gestionnaire limité */
  static voitFinancesFerme(): boolean { return !this.estPersonnel() && !this.estContenu() && !this.gestionnaireLimite(); }

  /** Inventaire et suppressions dans les modules, le stock et les équipements : administrateur ou gestionnaire */
  static gereExploitation(): boolean { return this.estAdmin() || this.estGestionnaire(); }

  /** Rédiger des articles du site : administrateurs et rôle Contenu */
  static redigeArticles(): boolean { return this.estAdmin() || this.estContenu(); }

  /** Synthèse, rapports, exports */
  static voitFinances(): boolean { return this.estAdmin() || this.estComptable(); }

  /** Saisir (vente, dépense, paiement, justificatif) : tous les rôles, comptable compris */
  static peutModifier(): boolean { return true; }

  /** Nom de l'utilisateur tel que le serveur l'enregistre dans « Saisi par » (nom, sinon e-mail) */
  static nomUtilisateur(): string {
    try {
      const u = JSON.parse(sessionStorage.getItem('authUser') || 'null');
      return (u?.displayName || u?.email || '').trim();
    } catch { return ''; }
  }

  /**
   * Modifier ou annuler une saisie (même règle que le serveur, qui fait foi) :
   * administrateur → toutes ; comptable → les siennes ; personnel → les siennes des dernières 24 h.
   */
  static peutModifierSaisie(t: { createdBy?: string; createdAt?: string }): boolean {
    if (!this.peutModifier()) return false;
    if (this.estAdmin()) return true;
    const moi = this.nomUtilisateur().toLowerCase();
    const auteur = (t.createdBy || '').trim().toLowerCase();
    const cree = t.createdAt ? new Date(t.createdAt).getTime() : 0;
    if (!moi || auteur !== moi) return false;
    return this.estComptable() || this.estGestionnaire() || (cree > 0 && Date.now() - cree < 24 * 3600 * 1000);
  }

  /** Mouvement de stock, intervention d'entretien : le gestionnaire les corrige toutes */
  static peutModifierSaisieModule(t: { createdBy?: string; createdAt?: string }): boolean {
    return this.gereExploitation() || this.peutModifierSaisie(t);
  }
}

/** Articles du site : administrateurs (ADMIN, ADMIN_FERME) ; le serveur refuse aussi (403) */
@Injectable({ providedIn: 'root' })
export class ArticlesGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(): boolean | UrlTree {
    return RoleUtil.redigeArticles() ? true : this.router.parseUrl('/dashboard');
  }
}

/** Rôle Contenu : seuls les articles du site lui sont ouverts (le serveur refuse aussi le reste) */
@Injectable({ providedIn: 'root' })
export class ContenuGuard implements CanActivateChild {
  constructor(private router: Router) {}
  canActivateChild(_r: ActivatedRouteSnapshot, state: RouterStateSnapshot): boolean | UrlTree {
    if (!RoleUtil.estContenu() || state.url.startsWith('/articles')) { return true; }
    return this.router.parseUrl('/articles');
  }
}

/** Saisies et dépenses du personnel à valider : administrateur ou gestionnaire ; le comptable consulte les dépenses */
@Injectable({ providedIn: 'root' })
export class GestionGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(): boolean | UrlTree {
    return RoleUtil.gereExploitation() || RoleUtil.estComptable() ? true : this.router.parseUrl('/dashboard');
  }
}

/** Écrans réservés à l'administrateur (synthèse, rapports, exports) : le serveur refuse aussi (403) */
@Injectable({ providedIn: 'root' })
export class AdminFermeGuard implements CanActivate {
  constructor(private router: Router) {}
  canActivate(): boolean | UrlTree {
    return RoleUtil.voitFinances() ? true : this.router.parseUrl('/finances');
  }
}

/** Libellés et module financier (sourceModule) des postes */
export const POSTES: Record<string, { libelle: string; sourceModule: string }> = {
  BASSINS: { libelle: 'Bassins', sourceModule: 'PISCICULTURE' },
  POULAILLER: { libelle: 'Poulailler', sourceModule: 'VOLAILLE' },
  CHEPTEL: { libelle: 'Cheptel', sourceModule: 'CHEPTEL' },
  CULTURES: { libelle: 'Cultures', sourceModule: 'MARAICHAGE' },
};

/** « Pisciculture et Poulailler » */
export function libellePostes(postes: string[]): string {
  const l = postes.map(p => POSTES[p]?.libelle || p);
  return l.length <= 1 ? (l[0] || '') : l.slice(0, -1).join(', ') + ' et ' + l[l.length - 1];
}
